/**
 * Textos interpretativos para el PDF de cultura organizacional (CVF).
 * No modifica puntuaciones ni alineación; solo narrativa orientativa.
 */

import type { CultureScore } from '@/lib/cultureEngine';

type DimId = 'Clan' | 'Adocracia' | 'Mercado' | 'Jerarquía';

const DIMS: { id: DimId; key: keyof CultureScore }[] = [
  { id: 'Clan', key: 'clan' },
  { id: 'Adocracia', key: 'adhocracy' },
  { id: 'Mercado', key: 'market' },
  { id: 'Jerarquía', key: 'hierarchy' },
];

function dominantDimension(score: CultureScore): DimId {
  const ranked = [...DIMS].sort((a, b) => score[b.key] - score[a.key]);
  return ranked[0].id;
}

function mainTension(actual: CultureScore, desired: CultureScore): { label: DimId; gap: number } {
  let best: { label: DimId; gap: number } = { label: 'Clan', gap: 0 };
  for (const { id, key } of DIMS) {
    const gap = desired[key] - actual[key];
    if (Math.abs(gap) > Math.abs(best.gap)) best = { label: id, gap };
  }
  return best;
}

export function buildCultureExecutiveSummaryText(
  actual: CultureScore,
  desired: CultureScore,
  alignmentPercent: number
): string {
  const da = dominantDimension(actual);
  const dd = dominantDimension(desired);
  const tension = mainTension(actual, desired);
  const tensionPhrase =
    Math.abs(tension.gap) >= 5
      ? `La mayor tensión cuantitativa aparece en ${tension.label} (brecha deseada menos actual: ${tension.gap > 0 ? '+' : ''}${tension.gap.toFixed(1)} puntos), lo que suele concentrar la conversación sobre ajustes culturales.`
      : 'Las brechas entre lo actual y lo deseado son acotadas en todas las dimensiones, lo que sugiere un perfil relativamente alineado en términos agregados.';

  return (
    `Cultura actual dominante: ${da}. Cultura deseada dominante: ${dd}. ` +
    `Alineación reportada: ${alignmentPercent.toFixed(1)}%. ` +
    'Este porcentaje representa la cercanía entre la cultura percibida y la cultura deseada; no implica que una cultura sea mejor o peor en sí misma. ' +
    tensionPhrase +
    ' En síntesis, los resultados reflejan cómo las personas perciben hoy la organización frente a lo que consideran deseable para afrontar mejor las exigencias del trabajo; conviene integrar esta lectura con la estrategia y el modelo operativo.'
  );
}

export type GapInterpretationRow = {
  dimension: DimId;
  gap: number;
  text: string;
};

/** Brecha = deseada - actual (misma convención que la tabla del PDF). */
export function interpretCultureGaps(actual: CultureScore, desired: CultureScore): GapInterpretationRow[] {
  const rows: GapInterpretationRow[] = [];
  for (const { id, key } of DIMS) {
    const gap = desired[key] - actual[key];
    let text: string;
    if (gap > 10) {
      text = `La puntuación deseada supera a la actual en más de 10 puntos: esto puede interpretarse como una demanda de fortalecimiento en ${id} (mayor peso esperado en el perfil cultural).`;
    } else if (gap < -10) {
      text = `La puntuación actual supera a la deseada en más de 10 puntos: podría indicar un posible exceso relativo en ${id} o un área donde conviene moderar prácticas si la dirección busca otro equilibrio.`;
    } else if (Math.abs(gap) < 5) {
      text = `Brecha menor a 5 puntos: sugiere relativa estabilidad o coincidencia cultural en ${id} dentro de esta medición agregada.`;
    } else {
      text = `Brecha moderada (${gap > 0 ? '+' : ''}${gap.toFixed(2)} puntos): conviene explorar con equipos qué prácticas sustentan la diferencia y si requiere ajustes graduales.`;
    }
    rows.push({ dimension: id, gap, text });
  }
  return rows;
}

export function buildCultureOrganizationalImplications(actual: CultureScore, desired: CultureScore): string {
  const da = dominantDimension(actual);
  const dd = dominantDimension(desired);
  const parts: string[] = [];

  parts.push(
    `Combinación dominante actual (${da}) frente a la deseada (${dd}): la transición sugiere hacia dónde gravitan las expectativas colectivas. No implica que el perfil actual sea "incorrecto", sino que existe una dirección preferida para el futuro cercano.`
  );

  if (da === 'Jerarquía' && dd === 'Adocracia') {
    parts.push(
      'Un patrón frecuente con jerarquía alta en lo actual y adocracia alta en lo deseado sugiere tensión entre estructura, control y estabilidad por un lado, e innovación, flexibilidad y autonomía por el otro. Conviene transitar con pilotos, claridad de reglas y delegación gradual para no generar caos ni resistencia.'
    );
  }
  if (actual.market >= 28 && desired.market < actual.market - 3) {
    parts.push(
      'Un mercado alto en lo actual con menor énfasis deseado podría indicar que la orientación a resultados y presión por desempeño se percibe intensa; si la organización busca más equilibrio, se recomienda revisar metas, ritmos y apoyo.'
    );
  }
  if (actual.clan >= 28) {
    parts.push(
      'Un clan alto en lo actual suele asociarse a cohesión, pertenencia y colaboración; puede ser un activo si se acompaña de claridad de roles y gestión del desempeño.'
    );
  }
  if (desired.adhocracy >= 28 && actual.adhocracy < desired.adhocracy - 5) {
    parts.push(
      'Expectativa elevada hacia adocracia: innovación, adaptación y autonomía; conviene alinear recursos, tolerancia a error en pilotos y liderazgo que patrocine aprendizaje.'
    );
  }
  if (desired.hierarchy >= 28 && actual.hierarchy < desired.hierarchy - 5) {
    parts.push(
      'Mayor énfasis deseado en jerarquía: orden, procesos y estabilidad; podría reflejar necesidad de predictibilidad ante crecimiento o complejidad operativa.'
    );
  }
  if (desired.market >= 28 && actual.market < desired.market - 5) {
    parts.push(
      'Mayor énfasis deseado en mercado: claridad de resultados, clientes y competitividad; conviene reforzar indicadores y retroalimentación sin únicamente aumentar presión.'
    );
  }
  if (desired.clan >= 28 && actual.clan < desired.clan - 5) {
    parts.push(
      'Mayor énfasis deseado en clan: integración, confianza y participación; fortalecer comunicación interna y reconocimiento suele ser coherente con esta lectura.'
    );
  }

  if (parts.length === 1) {
    parts.push(
      'En conjunto, las cuatro dimensiones del modelo competitivo coexisten; la lectura depende del equilibrio numérico. Use este apartado junto con entrevistas o talleres para validar significado en su contexto.'
    );
  }

  return parts.join(' ');
}

export function buildCulturePracticalRecommendations(actual: CultureScore, desired: CultureScore): string {
  const tension = mainTension(actual, desired);
  const { label, gap } = tension;
  const chunks: string[] = [];

  if (gap > 5 && label === 'Adocracia') {
    chunks.push(
      'Brecha relevante hacia adocracia: crear espacios seguros de innovación, pilotos controlados con métricas, autonomía gradual por equipos maduros, mejora continua y canales formales para proponer ideas.'
    );
  }
  if (gap < -5 && label === 'Jerarquía') {
    chunks.push(
      'Exceso relativo de jerarquía respecto a lo deseado: revisar burocracia, simplificar trámites internos, delegar decisiones operativas y reducir controles que no agreguen valor.'
    );
  }
  if (gap > 5 && label === 'Clan') {
    chunks.push(
      'Fortalecimiento deseado del clan: impulsar comunicación bidireccional, reconocimiento específico, actividades de integración y liderazgo participativo con límites claros de rendimiento.'
    );
  }
  if (gap > 5 && label === 'Mercado') {
    chunks.push(
      'Fortalecimiento deseado del mercado: clarificar metas e indicadores, retroalimentación periódica de desempeño y visibilidad del vínculo entre trabajo y cliente o resultados clave.'
    );
  }
  if (chunks.length === 0) {
    chunks.push(
      'Con brechas acotadas, se recomienda mantener diálogo estratégico sobre cultura, revisar decisiones de estructura y procesos que impacten el día a día, y monitorear en la siguiente medición si las prioridades siguen siendo las mismas.'
    );
  }

  if (desired.adhocracy - actual.adhocracy > 8 && !chunks.some((c) => c.includes('adocracia'))) {
    chunks.push(
      'Además, si la organización aspira a más adocracia: combine experimentación acotada con gobierno de riesgos y comunicación transparente sobre aprendizajes.'
    );
  }

  return chunks.join(' ');
}

export function culturePdfExecutiveClosing(): string {
  return (
    'El resultado no debe interpretarse como que una cultura sea buena o mala en sí misma, sino como una diferencia entre la cultura percibida actualmente y la cultura que los colaboradores consideran deseable para responder mejor a las exigencias del trabajo. ' +
    'Conviene contrastar estos números con la estrategia de negocio, el liderazgo real y las políticas vigentes antes de programar cambios profundos.'
  );
}
